import WebAPIUtils from 'js/WebAPIUtils';
import moment from 'moment-timezone';

import { SATELLITE_LAYERS, TIFF_NDRE, TIFF_NDVI } from 'js/constants/SatelliteLayers';

import { ADD_FIELDS } from 'js/reducers/AddFieldReducer';
import { getFieldByBundle } from 'js/helpers/StateInterpreters';

// Models
import Field from 'js/model/Field';
import Bounds from 'js/model/Bounds';
import APIConstants from '../APIConstants';
import { REMOVE_FIELD_FROM_SEASON } from './SeasonReducer';
import {
  overrideUserSetDate,
  SelectedDate,
  setDateByUser,
  setNewDateAutomatically,
} from './SelectedDate';

const EDIT_POLYGON = 'POLYGON_EDITED';
const HOLE_ADDED = 'HOLE_ADDED';
const HOLE_REMOVED = 'HOLE_REMOVED';

/* ============== //
||  ACTIONTYPES   ||
// ============== */

export const SELECT_FIELD = 'fieldsense/Field/SELECT_FIELD';
export const SELECT_FIELD_BY_ID = 'fieldsense/Field/SELECT_FIELD_BY_ID';
const DELETE_FIELD = 'fieldsense/Field/DELETE_FIELD';
const CHANGE_IMAGE_TYPE = 'fieldsense/Field/CHANGE_IMAGE_TYPE';
const SELECT_FIELD_AND_DATE = 'fieldsense/Field/SELECT_FIELD_AND_DATE';
const SET_DATE = 'fieldsense/Field/SET_DATE';
const USER_SET_DATE = 'fieldsense/Field/USER_SET_DATE';
const OVERRIDE_USER_SELECTED_DATE = 'fieldsense/Field/OVERRIDE_USER_SELECTED_DATE';
const RESET_DATE = 'fieldsense/Field/RESET_DATE';
const UPDATE_FIELD_NAME = 'fieldsense/Field/UPDATE_FIELD_NAME';
const FETCH_CROP_HEALTH = 'fieldsense/Field/FETCH_CROP_HEALTH';
const SET_SATELLITE_IMAGERY_SHOWN = 'fieldsense/Field/SET_SATELLITE_IMAGERY_SHOWN';
const FETCH_STATISTICS = 'fieldsense/Field/FETCH_STATISTICS';
const FETCH_FIELDS = 'fieldsense/FieldReducer/FETCH_FIELDS';
const FETCH_DATES = 'fieldsense/FieldReducer/FETCH_DATES';
const CONNECT_FIELDS_TO_WEATHER_STATION =
  'fieldsense/FieldReducer/CONNECT_FIELDS_TO_WEATHER_STATION';
const CONNECT_FIELD_TO_WEATHER_STATION = 'fieldsense/FieldReducer/CONNECT_FIELD_TO_WEATHER_STATION';
const CREATE_UPDATE_FIELD_SEASON = 'fieldsense/FieldReducer/CREATE_UPDATE_FIELD_SEASON';
const UPDATE_FIELD_SEASONS = 'FieldReducer/FieldReducer/UPDATE_FIELD_SEASONS';
const COPY_FIELDS_TO_SEASON = 'FieldReducer/FieldReducer/COPY_FIELDS_TO_SEASON';
const GET_FARM_FIELDS = 'fieldsense/FieldReducer/GET_FARM_FIELDS';
const FILTERED_FIELDS = 'fieldsense/Field/FILTERED_FIELDS';
const FILTERED_CROP_TYPES = 'fieldsense/Field/FILTERED_CROP_TYPES';
const RESET_FILTERS = 'fieldsense/Field/RESET_FILTERS';
const SET_SELECTED_FIELD_DATES = 'fieldsense/Field/SET_SELECTED_FIELD_DATES';
const SET_DATES = 'fieldsense/Field/SET_DATES';
const SET_IS_DRAWING_FIELD_HOLE = 'fieldsense/Field/SET_IS_DRAWING_FIELD_HOLE';
const SELECT_FIELD_HOLE = 'fieldsense/Field/SELECT_FIELD_HOLE';
const DESELECT_FIELD_HOLE = 'fieldsense/Field/DESELECT_FIELD_HOLE';
const UPDATE_FIELD_POLYGON = 'fieldsense/Field/UPDATE_FIELD_POLYGON';
const SET_IS_EDITING_FIELD_BOUNDARIES = 'fieldsense/Field/SET_IS_EDITING_FIELD_BOUNDARIES';
const SET_IS_SPLITTING_FIELD = 'fieldsense/Field/SET_IS_SPLITTING_FIELD';
const SPLIT_FIELD = 'fieldsense/Field/SPLIT_FIELD';
const SET_IS_EDITING_MERGED_FIELD = 'fieldsense/Field/SET_IS_EDITING_MERGED_FIELD';
const MERGE_FIELDS = 'fieldsense/Field/MERGE_FIELDS';
const SET_BULK_PRESCRIPTION_FIELDS = 'fieldsense/Field/SET_BULK_PRESCRIPTION_FIELDS';

/* ============== //
||    HELPERS     ||
// ============== */

/**
 * Convert an array of field data from the server into a Map of fields.
 * @param {Array} fields holding the fields from the server.
 * @returns {Map} mapping from fieldId to a field
 */
export function createFieldsMap(fields): Map<number, Field> {
  const fieldsMap = new Map();

  if (fields != null && Array.isArray(fields)) {
    for (const f of fields) {
      let field = new Field();
      field = { ...field, ...f, fieldId: f.fieldId || f.id };
      if (f.polygonWkt) {
        const convertedArray = convertWktTo3DArray(f.polygonWkt);
        field.polygon = { coordinates: convertedArray };
      }
      if (f.bounds && typeof f.bounds === 'string') {
        field.bounds = Bounds.fromString(f.bounds);
      }
      fieldsMap.set(field.fieldId, field);
    }
  }

  return fieldsMap;
}

/**
 * Convert WKT string to array of coordinates
 * @param {string} wkt WKT string
 * @returns {Array} Array of coordinates
 */
export function convertWktTo3DArray(wkt: string) {
  wkt = wkt.replace('POLYGON ((', 'POLYGON((');
  const polygons = [];
  let startIdx = 'POLYGON('.length;

  while (true) {
    const startParen = wkt.indexOf('(', startIdx);
    if (startParen === -1) break;

    const endParen = wkt.indexOf(')', startParen);
    if (endParen == -1) break;

    const polygon = wkt.substring(startParen + 1, endParen).trim();
    startIdx = endParen + 1;
    polygons.push(polygon);
  }

  const fieldCoordinates = [];
  for (const polygonString of polygons) {
    const coords = polygonString.split(',');
    const coordinates = [];

    for (const coord of coords) {
      const latLon = coord.trim().split(' ');
      if (latLon.length === 2) {
        coordinates.push([parseFloat(latLon[0]), parseFloat(latLon[1])]);
      }
    }
    fieldCoordinates.push(coordinates);
  }

  return fieldCoordinates;
}

/**
 * Convert array of coordinates to WKT string
 * @param {Array} fieldCoordinates Array of coordinates
 * @returns {String} WKT string
 */
function convert3DArrayToWkt(fieldCoordinates) {
  let wkt = 'POLYGON((';
  for (let i = 0; i < fieldCoordinates.length; i++) {
    const polygon = fieldCoordinates[i];
    for (let j = 0; j < polygon.length; j++) {
      const coord = polygon[j];
      wkt += `${coord[0]} ${coord[1]},`;
    }
    wkt = wkt.slice(0, -1) + '),(';
  }
  wkt = wkt.slice(0, -2) + '))';
  return wkt;
}

/**
 * Convert an array of dates into a sorted map of dates
 * @param {Array} dates a list of all field images.
 * @returns {Map} The map containing all dates with images. This map is sorted by date!
 */
function createDateMap(dates) {
  const dateMap = new Map();

  if (dates != null && Array.isArray(dates)) {
    for (const date of dates) {
      dateMap.set(date, null);
    }
  }

  return dateMap;
}

// MARK: The vitality key should be changed
async function fetchCropHealthAsListWidthBounds(farm, date, fields) {
  if (!date) {
    return [];
  }

  const cropHealthData = await WebAPIUtils.getCropHealthForDate(farm, date);

  const result = [];
  for (const i in cropHealthData) {
    const id = Number(i);
    const healthObj = cropHealthData[id];
    const field = getFieldByBundle([...fields.values()], id);

    if (field == null) {
      continue;
    }

    const object = {
      ...healthObj,
      fieldId: field.fieldId,
      bounds: field.bounds,
      [TIFF_NDVI]: APIConstants.imagesBaseURL + healthObj['tiff'],
      [SATELLITE_LAYERS.VITALITY_NDVI]: APIConstants.imagesBaseURL + healthObj['vitality'],
      [SATELLITE_LAYERS.VARIATIONS_NDVI]: APIConstants.imagesBaseURL + healthObj['variations'],
      [SATELLITE_LAYERS.VISIBLE]:
        APIConstants.imagesBaseURL + healthObj[SATELLITE_LAYERS.VISIBLE.toLowerCase()],
    };

    if (healthObj['ndre'] != null) {
      object[TIFF_NDRE] = APIConstants.imagesBaseURL + healthObj['ndre']['tif'];
      object[SATELLITE_LAYERS.VITALITY_NDRE] =
        APIConstants.imagesBaseURL + healthObj['ndre']['png'];
      object[SATELLITE_LAYERS.VARIATIONS_NDRE] =
        APIConstants.imagesBaseURL + healthObj['ndre']['variations'];
    }

    result.push(object);
  }
  return result;
}

function getUnprocessedFields(fields) {
  const unprocessed = [];
  // Look for any fields that are n ot ready
  fields.forEach((field) => {
    if (!field.ready) {
      unprocessed.push(field);
    }
  });

  return unprocessed;
}

function getSelectedFieldDates(stats) {
  const dates = [];

  stats.forEach((item) => {
    if (dates.indexOf(item.date) === -1) {
      dates.push(item.date);
    }
  });

  dates.sort((a, b) => {
    return moment(a).diff(moment(b));
  });

  return dates;
}

function createFilteredFieldMap(state, payload) {
  const filteredFields = new Map(state.filteredFields);

  if (payload.hasOwnProperty('field')) {
    if (payload.checked) {
      if (!filteredFields.has(payload.field.fieldId)) {
        filteredFields.set(payload.field.fieldId, payload.field);
      }
    } else {
      if (filteredFields.has(payload.field.fieldId)) {
        filteredFields.delete(payload.field.fieldId);
      }
    }
  } else {
    if (payload.checked) {
      const fields = Array.from(state.fields.values()).filter(
        (field) => field.crop === payload.cropType
      );
      fields.forEach((field) => filteredFields.set(field.fieldId, field));
    } else {
      const fields = Array.from(state.filteredFields.values()).filter(
        (field) => field.crop === payload.cropType
      );
      fields.forEach((field) => filteredFields.delete(field.fieldId));
    }
  }

  return filteredFields;
}

function createFilteredCropTypes(state, payload) {
  let filteredCropTypes = new Array(...state.filteredCropTypes);
  if (payload.hasOwnProperty('cropType')) {
    if (payload.checked) {
      if (!filteredCropTypes.includes(payload.cropType)) {
        filteredCropTypes.push(payload.cropType);
      }
    } else {
      if (filteredCropTypes.includes(payload.cropType)) {
        filteredCropTypes = filteredCropTypes.filter((cropType) => cropType !== payload.cropType);
      }
    }
  } else {
    const cropType = payload.field.crop;
    if (payload.checked) {
      const fields = Array.from(state.fields.values()).filter((field) => field.crop === cropType);
      const filteredFields = Array.from(state.filteredFields.values()).filter(
        (field) => field.crop === cropType
      );
      if (fields.length === filteredFields.length + 1) {
        if (!filteredCropTypes.includes(cropType)) {
          filteredCropTypes.push(cropType);
        }
      }
    } else {
      if (filteredCropTypes.includes(cropType)) {
        filteredCropTypes = filteredCropTypes.filter((c) => c !== cropType);
      }
    }
  }

  return filteredCropTypes;
}

/* ============== //
||    ACTIONS    ||
// ============== */

export function getFarmFields(farmId) {
  return {
    type: GET_FARM_FIELDS,
    payload: new Promise((resolve, reject) => {
      WebAPIUtils.getFarmFields(farmId)
        .then((result) => {
          resolve(normalizeFarmFields(result));
        })
        .catch((error) => {
          reject(error);
        });
    }),
  };
}

export function fetchFields(seasonId, silent) {
  return {
    type: FETCH_FIELDS,
    payload: WebAPIUtils.getSeasonFields(seasonId),
    meta: {
      silent: silent,
      seasonId: seasonId,
    },
  };
}

export function fetchDatesForAllYears(farmId) {
  return async (dispatch, getState) => {
    let lengthBeforeFetch = 0;
    let lengthAfterFetch = 1;
    const currentYear = new Date().getFullYear();
    let years = [currentYear, currentYear - 1, currentYear - 2];
    while (lengthAfterFetch > lengthBeforeFetch && years[2] >= 2000) {
      lengthBeforeFetch = getState().field.dates.length;
      await dispatch(fetchDates(farmId, years));
      lengthAfterFetch = getState().field.dates.length;
      years = [years[2] - 1, years[2] - 2, years[2] - 3];
    }
  };
}

export function fetchDates(farmId, years) {
  return {
    type: FETCH_DATES,
    payload: WebAPIUtils.getDates(farmId, years),
  };
}

export function fetchCropHealth(farmId, date, fields) {
  return {
    type: FETCH_CROP_HEALTH,
    payload: fetchCropHealthAsListWidthBounds(farmId, date, fields),
    meta: date,
  };
}

/**
 * @param date
 *
 * Select a date. If previously selected date was by the user, this date will not be selected.
 * If you need to override a user selected date, use overrideUserSelectedDate.
 *
 */
export function setDate(date) {
  return {
    type: SET_DATE,
    payload: date,
  };
}

/**
 * @param date Newly selected date by the system.
 *
 * Use this function when you want to override a user selected date. If that's not the case, use `setDate`.
 */
export function overrideUserSelectedDate(date) {
  return {
    type: OVERRIDE_USER_SELECTED_DATE,
    payload: date,
  };
}

/**
 * @param date Date that was selected by the user.
 *
 * Date has been selected by the user. If that's not the case, see setDate.
 */
export function userSetDate(date) {
  return {
    type: USER_SET_DATE,
    payload: date,
  };
}

export function setDates(dates) {
  return {
    type: SET_DATES,
    payload: dates,
  };
}

export function resetDate() {
  return {
    type: RESET_DATE,
  };
}

export function selectField(field) {
  return {
    type: SELECT_FIELD,
    payload: field,
  };
}

export function selectFieldById(fieldId) {
  return {
    type: SELECT_FIELD_BY_ID,
    payload: fieldId,
  };
}

export function changeImageType(type) {
  return {
    type: CHANGE_IMAGE_TYPE,
    payload: type,
  };
}

export function selectFieldAndDate(field, date) {
  return {
    type: SELECT_FIELD_AND_DATE,
    payload: { date, field },
  };
}

export function setFilteredField(field, checked) {
  return {
    type: FILTERED_FIELDS,
    payload: { checked: checked, field: field },
  };
}

export function setFilteredCropType(cropType, checked) {
  return {
    type: FILTERED_CROP_TYPES,
    payload: { checked: checked, cropType: cropType },
  };
}

export function resetFilters() {
  return {
    type: RESET_FILTERS,
  };
}

export function updateFieldName(name, fieldId) {
  return {
    type: UPDATE_FIELD_NAME,
    payload: WebAPIUtils.putFieldName(name, fieldId),
    meta: { name, fieldId },
  };
}

export function deleteField(field) {
  return {
    type: DELETE_FIELD,
    payload: WebAPIUtils.deleteField(field.fieldId),
    meta: { field: field },
  };
}

export function setShowSatelliteImagery(shown) {
  return {
    type: SET_SATELLITE_IMAGERY_SHOWN,
    payload: shown,
  };
}

export function fetchStatisticsForField(bundleId, imageType) {
  const type =
    imageType === SATELLITE_LAYERS.VITALITY_NDRE || imageType === SATELLITE_LAYERS.VARIATIONS_NDRE
      ? 'ndre'
      : 'ndvi';
  return {
    type: FETCH_STATISTICS,
    payload: WebAPIUtils.getBundleStats(bundleId, type),
  };
}

export function setSelectedFieldDates(dates) {
  return {
    type: SET_SELECTED_FIELD_DATES,
    payload: dates,
  };
}

export function createUpdateFieldSeason(fieldId, seasonId, crop, sowing, cropVariation) {
  const body = {
    crop: crop,
    sowing: sowing,
    cropVariation: cropVariation,
  };

  return {
    type: CREATE_UPDATE_FIELD_SEASON,
    payload: WebAPIUtils.createUpdateFieldSeason(fieldId, seasonId, body),
    meta: {
      fieldId,
      seasonId,
      crop,
      sowing,
      cropVariation,
    },
  };
}

export function updateFieldSeasons(seasonId, fieldSeasons) {
  return async (dispatch) => {
    const body = {
      seasonId: seasonId,
      seasons: fieldSeasons,
    };

    const payload = WebAPIUtils.updateFieldSeasons(body);

    const result = await dispatch({
      type: UPDATE_FIELD_SEASONS,
      payload: payload,
      meta: body,
    });

    if (result) {
      return dispatch(fetchFields(seasonId, false));
    }
  };
}

export function copySelectedFieldsToSeason(targetSeasonId, fieldsToCopy) {
  return async (dispatch) => {
    const body = {
      targetSeasonId,
      fieldsToCopy: fieldsToCopy,
    };

    const payload = WebAPIUtils.copySelectedFieldsToSeason(body);
    const result = await dispatch({
      type: UPDATE_FIELD_SEASONS,
      payload: payload,
      meta: body,
    });

    if (result) {
      return dispatch(fetchFields(targetSeasonId, false));
    }
  };
}

export function copyFieldsToSeason(toSeasonId, fromSeasonId) {
  return async (dispatch) => {
    const body = {
      targetSeasonId: toSeasonId,
      sourceSeasonId: fromSeasonId,
    };

    const payload = WebAPIUtils.copyFieldsToSeason(body);

    const result = await dispatch({
      type: COPY_FIELDS_TO_SEASON,
      payload: payload,
      meta: body,
    });

    if (result) {
      return dispatch(fetchFields(toSeasonId, false));
    }
  };
}

export function connectFieldsToWeatherStation(fieldIds, deviceId) {
  return {
    type: CONNECT_FIELDS_TO_WEATHER_STATION,
    payload: WebAPIUtils.connectFieldsToDevice(fieldIds, deviceId),
    meta: {
      fieldIds,
      deviceId,
    },
  };
}

export function connectFieldToWeatherStation(fieldId, deviceId) {
  return {
    type: CONNECT_FIELD_TO_WEATHER_STATION,
    payload: WebAPIUtils.updateFieldDevice(fieldId, deviceId),
    meta: {
      fieldId,
      deviceId,
    },
  };
}

export function setIsDrawingFieldHole(isDrawing) {
  return {
    type: SET_IS_DRAWING_FIELD_HOLE,
    payload: isDrawing,
  };
}

export function selectFieldHole(fieldHole) {
  return {
    type: SELECT_FIELD_HOLE,
    payload: fieldHole,
  };
}

export function deselectFieldHole() {
  return {
    type: DESELECT_FIELD_HOLE,
  };
}

export function addFieldHole(fieldId, seasonId, fieldCoordinates, poly) {
  const nodes = [];
  poly.forEach((elem) => {
    const node = [];
    node.push(elem.longitude);
    node.push(elem.latitude);
    nodes.push(node);
  });
  const newCoordinates = [...fieldCoordinates, nodes];
  const newPolygonAsWKT = convert3DArrayToWkt(newCoordinates);
  const newPolygonBody = {
    polygon: newPolygonAsWKT,
    seasonId: seasonId,
    polygonChange: HOLE_ADDED,
  };

  return {
    type: UPDATE_FIELD_POLYGON,
    payload: WebAPIUtils.updateFieldPolygon(fieldId, newPolygonBody),
    meta: { fieldId: fieldId },
  };
}

export function removeFieldHole(fieldId, seasonId, fieldHole, fieldCoordinates) {
  fieldCoordinates = fieldCoordinates.filter((coord) => {
    return coord !== fieldHole;
  });
  const newPolygonAsWKT = convert3DArrayToWkt(fieldCoordinates);
  const newPolygonBody = {
    polygon: newPolygonAsWKT,
    seasonId: seasonId,
    polygonChange: HOLE_REMOVED,
  };

  return {
    type: UPDATE_FIELD_POLYGON,
    payload: WebAPIUtils.updateFieldPolygon(fieldId, newPolygonBody),
    meta: { fieldId: fieldId },
  };
}

export function setIsEditingFieldBoundaries(isEditing) {
  return {
    type: SET_IS_EDITING_FIELD_BOUNDARIES,
    payload: isEditing,
  };
}

export function saveEditedFieldBoundaries(fieldId, seasonId, newFieldBoundaries, fieldHolesToKeep) {
  const newPolygonCoordinates = [...newFieldBoundaries, ...fieldHolesToKeep];
  const newPolygonAsWKT = convert3DArrayToWkt(newPolygonCoordinates);
  const newPolygonBody = {
    polygon: newPolygonAsWKT,
    seasonId: seasonId,
    polygonChange: EDIT_POLYGON,
  };

  return {
    type: UPDATE_FIELD_POLYGON,
    payload: WebAPIUtils.updateFieldPolygon(fieldId, newPolygonBody),
    meta: { fieldId: fieldId },
  };
}

export function setIsSplittingField(isSplitting) {
  return {
    type: SET_IS_SPLITTING_FIELD,
    payload: isSplitting,
  };
}

// WAIT TILL BACKEND IS READY TO CHANGE MEF
export function splitField(fieldId, parentPolygon, seasonId, newFieldAObject, newFieldObjectB) {
  const fieldACoordinates = convert3DArrayToWkt([...newFieldAObject.coordinates]);
  const fieldBCoordinates = convert3DArrayToWkt([...newFieldObjectB.coordinates]);

  const newFieldA = {
    polygon: fieldACoordinates,
    name: newFieldAObject.name,
    mef: newFieldAObject.mef,
  };
  const newFieldB = {
    polygon: fieldBCoordinates,
    name: newFieldObjectB.name,
    mef: newFieldObjectB.mef,
  };

  const splitFieldBody = { seasonId, parentPolygon, newFields: [newFieldA, newFieldB] };
  return {
    type: SPLIT_FIELD,
    payload: WebAPIUtils.splitField(fieldId, splitFieldBody),
    meta: {
      fieldId: fieldId,
      updatedFieldObject: newFieldAObject,
      newFieldObject: newFieldObjectB,
    },
  };
}

export function setIsEditingMergedField(isEditing) {
  return {
    type: SET_IS_EDITING_MERGED_FIELD,
    payload: isEditing,
  };
}

// WAIT TILL BACKEND IS READY TO CHANGE TO MEF
export function mergeFields(parentFieldIds, seasonId, newCoords, newName, newMef) {
  const polygon = convert3DArrayToWkt(newCoords);
  const name = newName;
  const mef = newMef;

  const mergeFieldsBody = {
    parentFieldIds,
    seasonId,
    polygon,
    name,
    mef,
  };

  return {
    type: MERGE_FIELDS,
    payload: WebAPIUtils.mergeFields(mergeFieldsBody),
    meta: { parentFieldIds },
  };
}

export function setBulkPrescriptionFields(fields) {
  return {
    type: SET_BULK_PRESCRIPTION_FIELDS,
    payload: fields,
  };
}

function normalizeFarmFields(raw = []) {
  return raw.map((f) => ({
    ...new Field(),
    ...f,
    fieldId: f.fieldId || f.id,
    polygon: f.polygonWkt ? { coordinates: convertWktTo3DArray(f.polygonWkt) } : f.polygon,
  }));
}

/* ============== //
||     REDUCER    ||
// ============== */

const initState = {
  farmFields: [],
  fields: new Map(),
  images: new Map(),
  imageType: SATELLITE_LAYERS.VITALITY_NDVI,
  date: null,
  dates: [],
  fieldDates: [],
  selectedFieldStatistics: [],
  selectedFieldDates: [],
  selectedField: null,
  filteredFields: new Map(),
  filteredCropTypes: [],
  fieldsLoaded: false,
  showSatelliteImagery: true,
  showVariations: false,
  unprocessedFields: [],
  isDrawingFieldHole: false,
  selectedFieldHole: null,
  isEditingFieldBoundaries: false,
  isSplittingField: false,
  isEditingMergedField: false,
  mergedFields: {},
  bulkPrescriptionFields: [],
};

export default function reducer(state = initState, action) {
  switch (action.type) {
    case FETCH_STATISTICS + '_FULFILLED': {
      state = {
        ...state,
        selectedFieldStatistics: action.payload,
        selectedFieldDates: getSelectedFieldDates(action.payload),
      };
      break;
    }

    case SET_SELECTED_FIELD_DATES: {
      state = { ...state, selectedFieldDates: action.payload };
      break;
    }

    case GET_FARM_FIELDS + '_FULFILLED': {
      state = {
        ...state,
        farmFields: action.payload,
      };
      break;
    }

    case FETCH_FIELDS + '_PENDING':
      if (!action.meta.silent) {
        state = { ...state, fieldsLoaded: false };
      }
      break;

    case FETCH_FIELDS + '_FULFILLED': {
      const fields = action.payload && action.payload.fields;
      const silent = action.meta && action.meta.silent;

      if (fields) {
        const fieldsMap = createFieldsMap(fields);

        if (silent) {
          // only update state if the number of unprocessed fields changed
          const unprocessedFields = getUnprocessedFields(fieldsMap);

          if (unprocessedFields < state.unprocessedFields) {
            state = { ...state, fields: fieldsMap, unprocessedFields: unprocessedFields };
          } else {
            state = { ...state, unprocessedFields: unprocessedFields };
          }
        } else {
          state = {
            ...state,
            fields: fieldsMap,
            fieldsLoaded: true,
            unprocessedFields: getUnprocessedFields(fieldsMap),
          };
        }
      }
      break;
    }

    case FETCH_FIELDS + '_REJECTED':
      state = { ...state, fieldsLoaded: true };
      break;

    case FETCH_DATES + '_FULFILLED': {
      const dates = action.payload;
      const images = createDateMap(dates);

      state = { ...state, images: images };
      state.dates =
        [...state.dates, ...dates].sort((a, b) => {
          return new Date(a) - new Date(b);
        }) || [];
      state.fieldDates = [...state.dates] || [];
      state.date = state.date ? state.date : state.dates[state.dates.length - 1];
      // state.showSatelliteImagery = state.dates.length !== 0;
      break;
    }

    case DELETE_FIELD + '_FULFILLED': {
      const fields = new Map([...state.fields]);
      fields.delete(action.meta.field.fieldId);
      state = {
        ...state,
        fields: fields,
        unprocessedFields: getUnprocessedFields(fields),
      };
      break;
    }

    case FETCH_CROP_HEALTH + '_FULFILLED': {
      if (!action.meta) {
        break;
      }

      const images = new Map([...state.images]);
      images.set(action.meta, action.payload);

      state = {
        ...state,
        images: images,
      };
      break;
    }

    case SET_DATE: {
      state = { ...state, date: setNewDateAutomatically(action.payload, state.date) };
      break;
    }

    case USER_SET_DATE: {
      state = { ...state, date: setDateByUser(action.payload) };
      break;
    }

    case OVERRIDE_USER_SELECTED_DATE: {
      state = { ...state, date: overrideUserSetDate(action.payload) };
      break;
    }

    case SET_DATES: {
      state = { ...state, dates: action.payload };
      break;
    }

    case RESET_DATE:
      state = { ...state, date: null };
      break;

    case SELECT_FIELD: {
      state = { ...state, selectedField: action.payload };
      break;
    }

    case SELECT_FIELD_BY_ID: {
      const fieldId = action.payload;
      if (fieldId) {
        const field = state.fields.get(fieldId);
        if (field) {
          state = { ...state, selectedField: field };
        }
      }
      break;
    }

    case SELECT_FIELD_AND_DATE:
      state = {
        ...state,
        selectedField: { ...action.payload.field },
        date: setNewDateAutomatically(action.payload.date, state.date),
      };
      break;

    case FILTERED_FIELDS: {
      state = {
        ...state,
        filteredCropTypes: createFilteredCropTypes(state, action.payload),
        filteredFields: createFilteredFieldMap(state, action.payload),
      };
      break;
    }

    case FILTERED_CROP_TYPES: {
      state = {
        ...state,
        filteredCropTypes: createFilteredCropTypes(state, action.payload),
        filteredFields: createFilteredFieldMap(state, action.payload),
      };
      break;
    }

    case RESET_FILTERS: {
      state = {
        ...state,
        filteredCropTypes: [],
        filteredFields: new Map(),
      };
      break;
    }

    case CHANGE_IMAGE_TYPE:
      state = { ...state, imageType: action.payload };
      break;

    case ADD_FIELDS + '_FULFILLED': {
      const fields = new Map([...state.fields, ...createFieldsMap(action.payload)]);
      state = { ...state, fields: fields };
      state.unprocessedFields = getUnprocessedFields(fields);
      break;
    }

    case SET_SATELLITE_IMAGERY_SHOWN:
      state = { ...state, showSatelliteImagery: action.payload };
      break;

    case UPDATE_FIELD_NAME + '_FULFILLED':
      state = {
        ...state,
        selectedField: { ...state.selectedField },
        fields: new Map(state.fields),
      };
      state.fields.get(action.meta.fieldId).name = action.meta.name;
      if (state.selectedField.fieldId === action.meta.fieldId) {
        state.selectedField.name = action.meta.name;
      }
      break;

    case CREATE_UPDATE_FIELD_SEASON + '_FULFILLED': {
      const { fieldId, crop, sowing, cropVariation } = action.meta;

      const fields = new Map([...state.fields]);
      const field: Field = fields.get(fieldId);

      field.crop = crop;
      field.sowing = sowing;
      field.cropVariation = cropVariation;

      state = { ...state, fields: fields, selectedField: field };

      break;
    }

    case CONNECT_FIELDS_TO_WEATHER_STATION + '_FULFILLED': {
      const { fieldIds, deviceId } = action.meta;

      state = { ...state, fields: new Map(state.fields) };

      state.fields.forEach((field) => {
        if (fieldIds.includes(field.fieldId)) {
          field.deviceId = deviceId;
        } else {
          if (field.deviceId === deviceId) {
            field.deviceId = null;
          }
        }
      });

      break;
    }

    case CONNECT_FIELD_TO_WEATHER_STATION + '_FULFILLED': {
      const { fieldId, deviceId } = action.meta;

      state = { ...state, fields: new Map(state.fields) };

      const field = state.fields.get(fieldId);
      field.deviceId = deviceId;

      if (state.selectedField && state.selectedField.fieldId === fieldId) {
        state.selectedField = { ...field };
      }

      break;
    }

    case REMOVE_FIELD_FROM_SEASON + '_FULFILLED': {
      return {
        ...state,
        fields: createFieldsMap(
          [...state.fields.values()].filter((f) => f.fieldId !== action.meta.fieldId)
        ),
      };
    }

    case SET_IS_DRAWING_FIELD_HOLE:
      state = {
        ...state,
        isDrawingFieldHole: action.payload,
        drawnFieldHolePolygon: action.payload ? state.drawnPolygon : null,
      };
      break;

    case SELECT_FIELD_HOLE:
      state = {
        ...state,
        selectedFieldHole: action.payload,
      };
      break;

    case DESELECT_FIELD_HOLE:
      state = {
        ...state,
        selectedFieldHole: null,
      };
      break;

    case UPDATE_FIELD_POLYGON + '_FULFILLED': {
      const updatedField = state.fields.get(action.payload.fieldId);

      updatedField.polygonWkt = action.payload.polygon;
      updatedField.polygon.coordinates = convertWktTo3DArray(action.payload.polygon);
      updatedField.bounds = Bounds.fromString(action.payload.bounds);
      updatedField.bundle = action.payload.bundle;
      updatedField.size = action.payload.size;

      const newImages = new Map(
        Array.from(state.images, ([date, images]) => [
          date,
          images.filter((img) => img.field_id !== action.payload.fieldId),
        ])
      );

      state = {
        ...state,
        selectedField: { ...updatedField },
        fields: new Map(state.fields),
        images: newImages,
      };

      break;
    }

    case SET_IS_EDITING_FIELD_BOUNDARIES:
      state = {
        ...state,
        isEditingFieldBoundaries: action.payload,
        editedFieldPolygon: action.payload ? state.drawnPolygon : null,
      };
      break;

    case SET_IS_SPLITTING_FIELD:
      state = {
        ...state,
        isSplittingField: action.payload,
      };
      break;

    case SPLIT_FIELD + '_FULFILLED': {
      // Remove parent field
      const newFieldsMap = [...state.fields.values()].filter(
        (f) => action.meta.fieldId !== f.fieldId
      );
      // Created fields
      const newFieldA = action.payload.childFields[0];
      const newFieldB = action.payload.childFields[1];

      newFieldsMap.push(newFieldA);
      newFieldsMap.push(newFieldB);

      // new fields map
      const fields = createFieldsMap(newFieldsMap);

      const newImages = new Map(
        Array.from(state.images, ([date, images]) => [
          date,
          images.filter((img) => img.field_id !== action.meta.fieldId),
        ])
      );

      state = {
        ...state,
        selectedField: fields.get(newFieldA.fieldId),
        fields: fields,
        images: newImages,
      };
      break;
    }

    case SET_IS_EDITING_MERGED_FIELD:
      state = {
        ...state,
        isEditingMergedField: action.payload.isEditingMergedField,
        mergedFields: action.payload.mergedFields ? action.payload.mergedFields : {},
      };
      break;

    case MERGE_FIELDS + '_FULFILLED': {
      // Remove parent fields
      const newFieldsMap = [...state.fields.values()].filter(
        (f) => !action.meta.parentFieldIds.includes(f.fieldId)
      );
      // Add merged field
      newFieldsMap.push(action.payload);
      const fields = createFieldsMap(newFieldsMap);

      const newImages = new Map(
        Array.from(state.images, ([date, images]) => [
          date,
          images.filter((img) => !action.meta.parentFieldIds.includes(img.field_id)),
        ])
      );

      state = {
        ...state,
        selectedField: { ...fields.get(action.payload.fieldId) },
        fields: fields,
        images: newImages,
      };
      break;
    }

    case SET_BULK_PRESCRIPTION_FIELDS: {
      state = {
        ...state,
        bulkPrescriptionFields: action.payload,
      };
      break;
    }

    default:
      break;
  }

  return state;
}
